/**
 * Marketing funnel detection — frontend companion to backend/funnels.py.
 *
 * We want every lead / purchase / user tagged with the landing page that
 * captured them (`flex`, `homeworkout`, …) so email + WhatsApp marketing
 * can route to the right per-funnel sequence.
 *
 * Detection order (first non-empty wins):
 *   1. Explicit `?funnel=` URL param  (the quiz page forwards this)
 *   2. `?utm_source=` URL param       (compat with existing FB Ads UTMs)
 *   3. `ff_source_funnel` cookie      (set by the landing page on mount)
 *   4. DEFAULT_FUNNEL fallback
 */
export const SOURCE_FUNNELS = ["flex", "homeworkout"];
export const DEFAULT_FUNNEL = "flex";

const FUNNEL_COOKIE = "ff_source_funnel";

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : "";
}

export function normalizeFunnel(value) {
  const v = String(value || "").trim().toLowerCase();
  return SOURCE_FUNNELS.includes(v) ? v : DEFAULT_FUNNEL;
}

// Stamp the funnel cookie for 180 days so returning visitors keep their
// original tag even if they land on /checkout directly.
export function stampFunnel(funnel) {
  const f = normalizeFunnel(funnel);
  try {
    const maxAge = 60 * 60 * 24 * 180;
    document.cookie = `${FUNNEL_COOKIE}=${encodeURIComponent(f)}; path=/; max-age=${maxAge}; samesite=lax`;
  } catch (_) { /* SSR-safe */ }
  return f;
}

export function detectFunnel() {
  if (typeof window === "undefined") return DEFAULT_FUNNEL;
  const params = new URLSearchParams(window.location.search || "");
  const fromParam = params.get("funnel");
  if (fromParam) return normalizeFunnel(fromParam);
  const utm = params.get("utm_source");
  if (utm && SOURCE_FUNNELS.includes(utm.toLowerCase())) return normalizeFunnel(utm);
  const fromCookie = readCookie(FUNNEL_COOKIE);
  if (fromCookie) return normalizeFunnel(fromCookie);
  return DEFAULT_FUNNEL;
}
